
import { useEffect, useState, useCallback } from "react";
import { ReportTemplate } from "@/types/reportTypes";
import { reportTemplateRepository } from "../repository/reportTemplateRepository";
import { handleError } from "@/utils/errorHandler";

export function useReportTemplateList(
  defaultTemplates: ReportTemplate[],
  organizationId?: string,
  filterState?: {
    selectedCategories: string[];
    setSelectedCategories: (v: string[]) => void;
    searchTerm: string;
    setSearchTerm: (v: string) => void;
  }
) {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [savedReports, setSavedReports] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filtering state - either use passed state or create internal state
  const [internalSelectedCategories, internalSetSelectedCategories] = useState<string[]>(["All Categories"]);
  const [internalSearchTerm, internalSetSearchTerm] = useState("");

  // Use either passed in filter state or internal state
  const selectedCategories = filterState?.selectedCategories || internalSelectedCategories;
  const setSelectedCategories = filterState?.setSelectedCategories || internalSetSelectedCategories;
  const searchTerm = filterState?.searchTerm || internalSearchTerm;
  const setSearchTerm = filterState?.setSearchTerm || internalSetSearchTerm;

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const templateData = await reportTemplateRepository.getTemplates(defaultTemplates);
      setTemplates(templateData);
    } catch (err) {
      setError("Failed to load default templates.");
      handleError(err, "Failed to load default templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [defaultTemplates]);

  const loadSavedReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reports = await reportTemplateRepository.getSavedReports(organizationId);
      setSavedReports(reports);
    } catch (err) {
      setError("Failed to load saved reports.");
      handleError(err, "Failed to load saved reports");
      setSavedReports([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (organizationId) {
      loadSavedReports();
    } else {
      setSavedReports([]);
      setLoading(false);
    }
  }, [organizationId, loadSavedReports]);

  // Filter templates based on selected categories and search term
  const filterTemplates = useCallback((items: ReportTemplate[]) => {
    let filtered = items;

    if (selectedCategories.length > 0 && !selectedCategories.includes("All Categories")) {
      filtered = filtered.filter((t) => selectedCategories.includes(t.category));
    }

    if (searchTerm.trim()) {
      const pattern = searchTerm.trim().toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.name.toLowerCase().includes(pattern) ||
          t.description.toLowerCase().includes(pattern)
      );
    }

    return filtered;
  }, [selectedCategories, searchTerm]);

  // Combine and filter templates (including saved reports)
  const allTemplates = [...templates, ...savedReports];
  const filteredTemplates = filterTemplates(allTemplates);

  return {
    templates: filteredTemplates,
    unfilteredTemplates: allTemplates,
    savedReports,
    loading,
    error,
    loadSavedReports,
    searchTerm,
    setSearchTerm,
    selectedCategories,
    setSelectedCategories,
  };
}
